import { useTranslate } from '@refinedev/core';
import { Button, Grid, Typography } from 'antd';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { ResultCard, fmtDay, type ToolLocale } from '../../components/ToolSection';
import {
  cardMeaning,
  cardName,
  profileOrder,
  profileVisible,
  sectorLabel,
  type ProfileRow,
  type ProfileValue,
} from './model';

/**
 * E36/E53 · the Values profile as a compact table (design PORTAL-SECTIONS-2026-10-22 §2.4 V4): one row per value — No., Value, On the
 * wheel, Questionnaire, Steps — READ-ONLY. The first five ranked values and the row carrying the chosen way show on load, the rest
 * behind "Show all (n)". Empty cells are "—". Below 768 px each row is a stacked block, no column lost.
 */
type Props = { view: ProfileRow | undefined; locale: ToolLocale };

const GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '32px minmax(220px, 3fr) minmax(170px, 2fr) minmax(120px, 1.2fr) minmax(140px, 1.5fr)',
  gap: '4px 16px',
  alignItems: 'start',
};

const dash = <Typography.Text type="secondary">—</Typography.Text>;

export const ValuesProfileCard = ({ view, locale }: Props) => {
  const t = useTranslate() as unknown as (k: string, o?: Record<string, unknown>) => string;
  const screens = Grid.useBreakpoint();
  const wide = screens.md !== false;
  const [showAll, setShowAll] = useState(false);

  const ordered = profileOrder(view?.values ?? []);
  const rows = profileVisible(ordered, showAll);

  const wheelCell = (v: ProfileValue): ReactNode =>
    v.wheelSectorName && v.current !== null && v.ideal !== null ? (
      <span data-testid={`values-hub-value-${v.value}-wheel`}>
        {t('values.hub.profile.wheelCell', {
          sector: sectorLabel(locale, v.wheelPreset, v.wheelSector ?? '', v.wheelSectorName),
          current: v.current,
          ideal: v.ideal,
          gap: v.gap,
        })}
      </span>
    ) : (
      dash
    );
  const vlqCell = (v: ProfileValue): ReactNode =>
    v.vlqGap !== null && v.vlqGapArea !== null ? (
      <span data-testid={`values-hub-value-${v.value}-vlq`}>
        {t('values.hub.profile.vlqCell', {
          area: t(`values.vlq.areas.${v.vlqGapArea}`),
          importance: v.importance,
          consistency: v.consistency,
          gap: v.vlqGap,
        })}
      </span>
    ) : (
      dash
    );
  const stepsCell = (v: ProfileValue): ReactNode => {
    const parts: ReactNode[] = [];
    v.actions.forEach((a) =>
      parts.push(
        <span key={a.actionId} data-testid={`values-hub-value-${v.value}-action`}>
          {a.action} ·{' '}
          {a.status === 'done'
            ? t('values.hub.profile.actionDone')
            : t('values.hub.profile.actionOpen')}
        </span>,
      ),
    );
    if (v.rose)
      parts.push(
        <span key="rose" data-testid={`values-hub-value-${v.value}-rose`}>
          {t('values.hub.profile.roseCell', {
            name: v.rose.name,
            mean: v.rose.cappedMean === null ? '–' : v.rose.cappedMean.toFixed(1),
          })}
        </span>,
      );
    if (v.activities.length)
      parts.push(
        <span key="act">
          {t('values.hub.profile.activitiesCount', { count: v.activities.length })}
        </span>,
      );
    if (v.links.length)
      parts.push(
        <span key="links" data-testid={`values-hub-value-${v.value}-links`}>
          {t('values.hub.profile.linksCount', { count: v.links.length })}
        </span>,
      );
    return parts.length ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{parts}</div>
    ) : (
      dash
    );
  };
  const nameCell = (v: ProfileValue): ReactNode => (
    <div>
      <Typography.Text strong>{cardName(v.value, v.cardText)}</Typography.Text>
      {cardMeaning(v.cardText) ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {' · '}
          {cardMeaning(v.cardText)}
        </Typography.Text>
      ) : null}
    </div>
  );

  const head = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
      {view?.lastSortAt ? (
        <Typography.Text type="secondary" data-testid="values-hub-last-sort">
          {t('values.hub.profile.lastSort', { date: fmtDay(view.lastSortAt, locale) })}
        </Typography.Text>
      ) : null}
      {view?.vlqComposite !== null && view?.vlqComposite !== undefined ? (
        <Typography.Text data-testid="values-hub-composite">
          {t('values.hub.profile.composite', { composite: view.vlqComposite })}
        </Typography.Text>
      ) : null}
    </div>
  );

  const hidden = ordered.length - rows.length;
  return (
    <ResultCard
      testId="values-hub-profile"
      role="result"
      collapsible
      summary={
        view?.lastSortAt
          ? t('values.hub.profile.lastSort', { date: fmtDay(view.lastSortAt, locale) })
          : null
      }
      title={t('values.hub.profile.title')}
      footer={
        hidden > 0 || showAll ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => setShowAll(!showAll)}
            data-testid="values-hub-profile-show-all"
          >
            {showAll
              ? t('pages.toolSection.showFewer')
              : t('pages.toolSection.showAll', { count: ordered.length })}
          </Button>
        ) : null
      }
    >
      {view && view.values.length === 0 ? (
        <Typography.Text type="secondary" data-testid="values-hub-profile-empty">
          {t('values.hub.profile.empty')}
        </Typography.Text>
      ) : null}
      {head}
      {rows.length && wide ? (
        <div style={{ ...GRID, marginBottom: 4 }}>
          {['', 'colValue', 'colWheel', 'colVlq', 'colSteps'].map((k, i) => (
            <Typography.Text key={i} type="secondary" style={{ fontSize: 12 }}>
              {k ? t(`values.hub.profile.${k}`) : ''}
            </Typography.Text>
          ))}
        </div>
      ) : null}
      {rows.map((v) => (
        <div
          key={v.value}
          data-testid={`values-hub-value-${v.value}`}
          style={{
            borderTop: '1px solid rgba(128,128,128,0.2)',
            padding: '6px 0',
            ...(wide ? GRID : { display: 'flex', flexDirection: 'column', gap: 4 }),
          }}
        >
          <span>{v.rank !== null ? v.rank : dash}</span>
          {nameCell(v)}
          {wide ? (
            <>
              {wheelCell(v)}
              {vlqCell(v)}
              {stepsCell(v)}
            </>
          ) : (
            <>
              <LabeledCell label={t('values.hub.profile.colWheel')}>{wheelCell(v)}</LabeledCell>
              <LabeledCell label={t('values.hub.profile.colVlq')}>{vlqCell(v)}</LabeledCell>
              <LabeledCell label={t('values.hub.profile.colSteps')}>{stepsCell(v)}</LabeledCell>
            </>
          )}
        </div>
      ))}
    </ResultCard>
  );
};

const LabeledCell = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {label}:{' '}
    </Typography.Text>
    {children}
  </div>
);
